import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

import { PageError, PageLoading } from "@/components/LiveState";
import { AgentCard, GlancePanel, IssuesPanel, SkillsPanel } from "@/components/agent/AgentParts";
import { aboutFacts, alloyFacts, clientIssues, connectionFacts, groupActiveSkills, issuesTone, skillFacts, teamFacts } from "@/components/agent/agentFacts";
import SkillsDialog from "@/components/agent/SkillsDialog";
import SkillActivityLog from "@/components/agent/SkillUpdatesFeed";
import { PageHeader } from "@/components/common";
import { useApp } from "@/context/AppContext";
import { useLiveResource } from "@/hooks/useLiveResource";
import { describeChecked } from "@/pages/ConnectedStack";
import QuestionsEntry from "@/components/growth/QuestionsEntry";
import { growthEnabled } from "@/growth/pilot";

export { groupActiveSkills };

// The summaries of other pages are read once per visit; those pages keep themselves fresh when opened.
const READ_ONCE = { refreshMs: 0 };
const NO_ARGS = [];

const sourceStatus = (resource) => (resource.data ? "ready" : resource.status === "error" ? "error" : "loading");

// On a phone the page is one column: who the agent is, what to fix, the summary, the skills, then their log.
// On a desktop the log runs under both columns at full width.
const COLUMN = "contents lg:flex lg:min-w-0 lg:flex-col lg:gap-4";

export default function AgentHome() {
  const { activeClient } = useApp();
  const profileResource = useLiveResource("getAgentProfile");
  const skillsResource = useLiveResource("getSkills");
  const connectionsResource = useLiveResource("getAgentConnections", NO_ARGS, READ_ONCE);
  const creditsResource = useLiveResource("getCredits", NO_ARGS, READ_ONCE);
  const teamResource = useLiveResource("getTeam", NO_ARGS, READ_ONCE);
  // The skills popup: closed, or open on a group (null lets it pick the one made for the brand).
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [skillsGroup, setSkillsGroup] = useState(null);

  const workspaceName = activeClient?.clientName ?? "this workspace";
  const reportedSkills = skillsResource.data?.skills;
  const reportedChanges = skillsResource.data?.changes;

  const about = useMemo(() => aboutFacts(profileResource.data, workspaceName), [profileResource.data, workspaceName]);
  const skills = useMemo(() => (skillsResource.data ? skillFacts(reportedSkills, reportedChanges) : null), [skillsResource.data, reportedSkills, reportedChanges]);
  const connections = useMemo(() => connectionFacts(connectionsResource.data, { describeChecked }), [connectionsResource.data]);
  const alloy = useMemo(() => alloyFacts(creditsResource.data), [creditsResource.data]);
  const team = useMemo(() => teamFacts(teamResource.data), [teamResource.data]);
  const issues = useMemo(() => clientIssues({ connections, alloy }), [connections, alloy]);

  const waitingForBoth = !profileResource.data
    && !skillsResource.data
    && (profileResource.status === "loading" || skillsResource.status === "loading");
  if (waitingForBoth) return <PageLoading label="Loading your agent…" />;

  const bothFailed = profileResource.status === "error" && skillsResource.status === "error";
  if (bothFailed) {
    return (
      <PageError
        error={profileResource.error || skillsResource.error}
        onRetry={() => Promise.all([profileResource.reload(), skillsResource.reload()])}
        title="Your agent is unavailable"
      />
    );
  }

  const skillsStatus = sourceStatus(skillsResource);
  const checked = { connections: sourceStatus(connectionsResource), credits: sourceStatus(creditsResource) };
  const sourcesIn = (status) => Object.keys(checked).filter((source) => checked[source] === status);
  const openSkills = (category) => { setSkillsGroup(category); setSkillsOpen(true); };

  return (
    <div data-testid="agent-home">
      <PageHeader
        testId="agent-home-header"
        title={about.name || "Your agent"}
        subtitle={`A simple view of the GrowthOS agent configured for ${workspaceName}.`}
      >
        {growthEnabled(activeClient?.clientSlug) ? <QuestionsEntry agentName={about.name} /> : null}
        <Link
          to="/playbooks"
          data-testid="agent-link-results"
          className="inline-flex items-center gap-2 rounded-lg bg-sm-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#002d6d]"
        >
          <Sparkles size={15} /> Get Better Results
        </Link>
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,468px)_minmax(0,1fr)] lg:items-start lg:gap-6">
        <div className={COLUMN}>
          <AgentCard about={about} skills={skills} tone={issuesTone(issues)} className="order-1 lg:order-none" />
          {profileResource.status === "error" ? (
            <p className="order-1 px-1 text-xs text-sm-ink/65 lg:order-none">The agent profile could not be loaded, so its name is unavailable. Refresh this page to retry.</p>
          ) : null}
          <IssuesPanel className="order-2 lg:order-none" issues={issues} checking={sourcesIn("loading")} unknown={sourcesIn("error")} />
        </div>

        <div className={COLUMN}>
          <GlancePanel
            className="order-3 lg:order-none"
            connections={connections}
            connectionsStatus={checked.connections}
            alloy={alloy}
            alloyStatus={checked.credits}
            team={team}
            teamStatus={sourceStatus(teamResource)}
          />
          <SkillsPanel className="order-4 lg:order-none" skills={skills} status={skillsStatus} onOpen={() => openSkills(null)} />
        </div>

        {skillsStatus !== "loading" ? (
          <SkillActivityLog
            className="order-5 lg:order-none lg:col-span-2"
            agentName={about.name}
            changes={reportedChanges}
            sourceStatus={skillsStatus === "error" ? "error" : "available"}
          />
        ) : null}
      </div>

      <SkillsDialog
        open={skillsOpen}
        group={skillsGroup}
        onGroup={setSkillsGroup}
        onClose={() => setSkillsOpen(false)}
        agentName={about.name}
        skills={skills}
        reportedSkills={reportedSkills}
        reportedChanges={reportedChanges}
      />
    </div>
  );
}
